import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { RosterPilotLogo } from '../../assets/RosterPilotLogo';
import { logout, resetPassword, verifyResetCode } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import './ResetPassword.css';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const { user } = useAuth();

  const [apiError, setApiError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [email, setEmail] = useState<string>('');
  const [resetComplete, setResetComplete] = useState(false);

  // Log out any existing user before allowing password reset
  useEffect(() => {
    if (user) {
      logout();
    }
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    mode: 'onSubmit',
  });

  const password = watch('password');

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setApiError('Invalid or missing reset link. Please request a new password reset.');
        setIsVerifying(false);
        return;
      }

      try {
        const userEmail = await verifyResetCode(oobCode);
        setEmail(userEmail);
      } catch (error: unknown) {
        const authError = error as { code?: string; message?: string };
        let errorMessage = 'Invalid or expired reset link';

        switch (authError.code) {
          case 'auth/expired-action-code':
            errorMessage = 'This reset link has expired. Please request a new one.';
            break;
          case 'auth/invalid-action-code':
            errorMessage = 'Invalid reset link. Please request a new password reset.';
            break;
          default:
            errorMessage = authError.message || errorMessage;
        }

        setApiError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!oobCode) return;

    setApiError('');
    setIsSubmitting(true);

    try {
      await resetPassword(oobCode, data.password);
      setResetComplete(true);
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string };
      let errorMessage = 'Failed to reset password';

      switch (authError.code) {
        case 'auth/expired-action-code':
          errorMessage = 'This reset link has expired. Please request a new one.';
          break;
        case 'auth/invalid-action-code':
          errorMessage = 'Invalid reset link. Please request a new password reset.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak. Please choose a stronger password.';
          break;
        default:
          errorMessage = authError.message || errorMessage;
      }

      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenApp = () => {
    window.location.href = 'rosterpilot://';
  };

  if (isVerifying) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="reset-password-logo">
            <RosterPilotLogo />
          </div>
          <div className="reset-password-loading">
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="reset-password-logo">
            <RosterPilotLogo />
          </div>

          <div className="reset-password-header">
            <h1 className="reset-password-title">Password Reset</h1>
            <p className="reset-password-subtitle">
              Your password has been successfully reset.
            </p>
          </div>

          <div className="reset-password-success">
            <p>You can now log in with your new password.</p>
          </div>

          <div className="reset-password-actions">
            <Button type="button" fullWidth onClick={handleOpenApp}>
              Open in RosterPilot
            </Button>

            <Link to="/login" className="reset-password-link">
              Continue to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (apiError && !email) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="reset-password-logo">
            <RosterPilotLogo />
          </div>

          <div className="reset-password-header">
            <h1 className="reset-password-title">Reset Password</h1>
          </div>

          <div className="reset-password-error-banner">{apiError}</div>

          <div className="reset-password-actions">
            <Link to="/forgot-password" className="reset-password-link">
              Request New Reset Link
            </Link>
            <Link to="/login" className="reset-password-link">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-screen">
      <div className="reset-password-container">
        <div className="reset-password-logo">
          <RosterPilotLogo />
        </div>

        <div className="reset-password-header">
          <h1 className="reset-password-title">Set New Password</h1>
          <p className="reset-password-subtitle">
            Enter a new password for <strong>{email}</strong>
          </p>
        </div>

        <form className="reset-password-form" onSubmit={handleSubmit(onSubmit)}>
          {apiError && (
            <div className="reset-password-error-banner">{apiError}</div>
          )}

          <div className="reset-password-inputs">
            <TextInput
              label="New Password"
              type="password"
              placeholder="******"
              autoComplete="new-password"
              error={errors.password?.message}
              disabled={isSubmitting}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
            />

            <TextInput
              label="Confirm Password"
              type="password"
              placeholder="******"
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              disabled={isSubmitting}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === password || 'Passwords do not match',
              })}
            />
          </div>

          <div className="reset-password-actions">
            <Button type="submit" fullWidth loading={isSubmitting}>
              Reset Password
            </Button>

            <Link to="/login" className="reset-password-link">
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};
